import { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/auth.service';
import PageHeader from '../components/common/PageHeader';
import { toTitleCase } from '../utils/formatters';
import { UserRole } from '../types';

function roleLabel(role?: UserRole) {
  if (!role) return 'User';
  if (role === UserRole.SUPER_ADMIN) return 'Super Admin';
  if (role === UserRole.ADMIN) return 'Admin';
  if (role === UserRole.HR) return 'HR';
  if (role === UserRole.DEALER || role === UserRole.AGENT) return 'Dealer';
  if (role === UserRole.CLIENT) return 'Client';
  if (role === UserRole.EMPLOYEE) return 'Employee';
  return String(role);
}

function roleColor(role?: UserRole): { color: string; bg: string } {
  if (!role) return { color: '#64748B', bg: '#F1F5F9' };
  if (role === UserRole.SUPER_ADMIN) return { color: '#B45309', bg: '#FEF3C7' };
  if (role === UserRole.ADMIN)       return { color: '#DC2626', bg: '#FEE2E2' };
  if (role === UserRole.HR)          return { color: '#7C3AED', bg: '#EDE9FE' };
  if (role === UserRole.DEALER || role === UserRole.AGENT) return { color: '#2563EB', bg: '#DBEAFE' };
  if (role === UserRole.CLIENT)      return { color: '#059669', bg: '#D1FAE5' };
  return { color: '#64748B', bg: '#F1F5F9' };
}

export function ProfileSettings() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Profile form
  const [name, setName] = useState<string>(user?.name || '');
  const [savingProfile, setSavingProfile] = useState(false);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const primaryRole = userRoles[0];
  const rc = roleColor(primaryRole);

  const handleSaveProfile = async () => {
    if (!name.trim() || name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    setSavingProfile(true);
    try {
      await authService.updateProfile({ name: name.trim() });
      await refreshUser();
      setSuccess('Profile updated');
    } catch (err: any) {
      setError(err?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) { setError('Current password is required'); return; }
    if (!newPassword || newPassword.length < 6) { setError('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('New password and confirmation do not match'); return; }
    if (currentPassword === newPassword) { setError('New password must be different from the current one'); return; }
    setChangingPassword(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      setSuccess('Password changed. Use the new password next time you sign in.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err?.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <Box>
      <PageHeader
        eyebrow="Account"
        title="Settings"
        subtitle="Update your profile and password."
      />

      <Grid container spacing={3}>
        {/* LEFT — profile summary */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: '#FFFFFF',
              border: '1px solid #E2E8F0',
              textAlign: 'center',
            }}
          >
            <Avatar
              sx={{
                width: 84,
                height: 84,
                mx: 'auto',
                mb: 1.5,
                bgcolor: '#EC4899',
                color: '#FFFFFF',
                fontSize: '2rem',
                fontWeight: 700,
                letterSpacing: '-0.02em',
              }}
            >
              {(user?.name || 'U').charAt(0).toUpperCase()}
            </Avatar>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#0F172A', letterSpacing: '-0.01em' }}>
              {toTitleCase(user?.name || 'User')}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748B', mt: 0.25 }}>
              {user?.email}
            </Typography>
            <Chip
              label={roleLabel(primaryRole)}
              size="small"
              sx={{
                mt: 1.25,
                bgcolor: rc.bg,
                color: rc.color,
                fontWeight: 700,
                fontSize: '0.72rem',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            />

            <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box display="flex" alignItems="center" gap={1.25}>
                <MailOutlineIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                <Typography sx={{ fontSize: '0.78rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.email || '—'}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1.25}>
                <BadgeOutlinedIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
                  {userRoles.length > 1 ? `${userRoles.length} roles` : roleLabel(primaryRole)}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1.25}>
                <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
                  Member since {memberSince}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1.25}>
                <VerifiedOutlinedIcon sx={{ fontSize: 16, color: user?.isVerified ? '#059669' : '#D97706' }} />
                <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
                  {user?.isVerified ? 'Email verified' : 'Email pending verification'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT — tabs */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 2, pt: 1, borderBottom: '1px solid #E2E8F0' }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                  minHeight: 42,
                  '& .MuiTab-root': {
                    minHeight: 42,
                    py: 1,
                    fontSize: '0.83rem',
                    fontWeight: 600,
                    color: '#64748B',
                    textTransform: 'none',
                    letterSpacing: '-0.01em',
                    '&.Mui-selected': { color: '#0F172A' },
                  },
                  '& .MuiTabs-indicator': { backgroundColor: '#4F46E5', height: 2.5, borderRadius: 1.25 },
                }}
              >
                <Tab icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Profile" />
                <Tab icon={<LockOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Security" />
              </Tabs>
            </Box>

            <Box sx={{ p: 3 }}>
              {/* PROFILE TAB */}
              {tab === 0 && (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A' }}>
                    Profile information
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mt: -1 }}>
                    Your email is used for sign-in and cannot be changed.
                  </Typography>

                  <TextField
                    label="Full name"
                    size="small"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />

                  <TextField
                    label="Email address"
                    size="small"
                    fullWidth
                    value={user?.email || ''}
                    disabled
                    helperText="Managed by the administrator"
                  />

                  <TextField
                    label="Role"
                    size="small"
                    fullWidth
                    value={userRoles.map(roleLabel).join(', ') || '—'}
                    disabled
                  />

                  <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={savingProfile || name.trim() === (user?.name || '').trim()}
                      onClick={handleSaveProfile}
                      startIcon={savingProfile ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        bgcolor: '#4F46E5',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        px: 2.25,
                        py: 0.85,
                        borderRadius: 1.5,
                        boxShadow: '0 2px 6px rgba(79, 70, 229, 0.22)',
                        '&:hover': { bgcolor: '#3730A3', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.32)' },
                      }}
                    >
                      {savingProfile ? 'Saving…' : 'Save changes'}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* SECURITY TAB */}
              {tab === 1 && (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0F172A' }}>
                    Change password
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mt: -1 }}>
                    Use a password with at least 6 characters. If you signed up via OTP and haven't set one yet, ask an admin to reset it for you.
                  </Typography>

                  <TextField
                    label="Current password"
                    size="small"
                    fullWidth
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowCurrent((s) => !s)} edge="end">
                            {showCurrent ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="New password"
                    size="small"
                    fullWidth
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    helperText="At least 6 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowNew((s) => !s)} edge="end">
                            {showNew ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Confirm new password"
                    size="small"
                    fullWidth
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    error={confirmPassword.length > 0 && confirmPassword !== newPassword}
                    helperText={confirmPassword.length > 0 && confirmPassword !== newPassword ? 'Passwords do not match' : ' '}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowConfirm((s) => !s)} edge="end">
                            {showConfirm ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={changingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword || newPassword.length < 6}
                      onClick={handleChangePassword}
                      startIcon={changingPassword ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <LockOutlinedIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        bgcolor: '#4F46E5',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        px: 2.25,
                        py: 0.85,
                        borderRadius: 1.5,
                        boxShadow: '0 2px 6px rgba(79, 70, 229, 0.22)',
                        '&:hover': { bgcolor: '#3730A3', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.32)' },
                      }}
                    >
                      {changingPassword ? 'Updating…' : 'Update password'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
      </Snackbar>
    </Box>
  );
}

export default ProfileSettings;
